import React, { useState } from "react";
import { useRecoilValue } from 'recoil';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Container, Typography, TextField, Button, IconButton, Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Select, MenuItem } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { Close, Cancel, Delete, Save, } from '@mui/icons-material';

import { userInfo } from "../../auth/AuthProvider";
import Dialog4Confirm from "./Dialog4Confirm";
import Dialog4Alert from "./Dialog4Alert";
import PropTypes from 'prop-types';
import { isDebug } from "../../utils";
import { Transition, UserType, UseYn } from './ControlUtils'
import { useMemberItemPutMutation, useCheckCompanyRegNoPost, useCheckMemberEmailPost, useMemberDetailPost, useMemberApprovalPatchMutation, useMemberDeleteMutation } from "../../hooks/useBoard/useMemberQuery";

/*
/// ControlWriteMember.js
 * 사용자 등록/수정 컴포넌트 --> 사용자 상세 정보
  * @param {Object} props - 컴포넌트 속성 
  * @param {string} props.memberId - 사용자 ID (수정 시 사용)
  * @param {boolean} props.open - 다이얼로그 열림 여부
  * @param {function} props.closeEditDialog - 다이얼로그 닫기 함수
  * @param {string} props.regFailYn - 등록 실패 여부 (Y/N)
  * @param {function} props.handleApproval - 사용자 승인 핸들러
  * @param {Object} props.refMessage - 메시지 표시를 위한 참조 객체
  * @returns {JSX.Element} 사용자 등록/수정 다이얼로그 컴포넌트
  * @description
  * 이 컴포넌트는 사용자의 상세 정보를 등록하거나 수정하는 기능을 제공합니다.
  * 사용자가 입력한 정보를 검증하고, 승인 여부에 따라 사용자 정보를 업데이트하거나
  * 삭제하는 기능을 포함합니다.
  * 사용자 정보는 이름, 이메일, DID, ConnectionId, SubscriptionId, 승인 여부, 사용 여부 등을 포함합니다.
  * 사용자 유형과 사용 여부는 드롭다운 메뉴로 선택할 수 있습니다.
  * 사용자 정보가 성공적으로 업데이트되면 알림 메시지를 표시합니다.
  * UseYn은 항상 'Y'로 설정되어 있습니다.
  * ApprovalYn은 'null'으로 설정되어 있으며, 사용자가 승인되면 'Y'로 변경됩니다.
*/
export default function ControlWriteMember (props) {
  const { memberId, open, closeEditDialog, regFailYn, handleApproval, refMessage} = props;
  const navigate = useNavigate();
  const auth = useRecoilValue(userInfo);
  const { t } = useTranslation();
  
  const isLoadingRef = React.useRef(false);
  const refConfirm = React.useRef(null);
  const refAlert = React.useRef(null);
  
  const [ pageTitle ] = React.useState(t('nav.manage4member'));
  const [ selectMemberType ] = React.useState(UserType);
  const [ selectApprovalYn ] = React.useState(UseYn);
  const [ selectUseYn ] = React.useState(UseYn);
  
  const defaultPostVlues = {MemberId: memberId, CompanyId: '', MemberName: '', MemberType: 'N', Email: '', Did: '', ConnectionId: '', SubscriptionId: '', ApprovalYn: 'N', UseYn: 'N'};
  const [editPost, setEditPost] = useState(defaultPostVlues);

  const { mutate: putMemberItem } = useMemberItemPutMutation();
  const { mutate: patchMemberApproval } = useMemberApprovalPatchMutation();
  const { mutate: deleteMmeber } = useMemberDeleteMutation();

  const { isError, isLoading, isFetching, isSuccess, error, data } = useMemberDetailPost(memberId);
  

  // 사용자 승인 핸들러
  const handleEditPost = () => {
    if(auth.userId === undefined || auth.userId === null || auth.userId === '') {
      refMessage.openMessage(`${t('error.notlogin')}`);
      setTimeout(() => {
        navigate(`/login`);
      }, process.env.REACT_APP_REDIRECT_TIME_WAIT);
    }
    else if (isValidation()) {
      refConfirm.current.showMessageBox(`${t('button.save')}`, `${t('alert.saveconfirm')}`, (result) => {
        if(result) {
          if (isLoadingRef.current)  return;
          isLoadingRef.current = true;

          // 저장하고
          if(memberId > 0)
          {
            patchMemberApproval(editPost, {
              onSuccess: () => {
                refAlert.current.showMessageBox(t('button.save'), t('message.updatecomplet'));
              },
              onError: (err) => {
                let msg = err?.response?.data.detail[0].msg || `${t('error.notprosess')}`;
                refAlert.current.showMessageBox(t('error.title'), msg);
              },
            });
          }
          else
          {
            putMemberItem(editPost, {
              onSuccess: () => {
                refAlert.current.showMessageBox(t('button.save'), t('message.savecomplet'));
              },
              onError: (err) => {
                let msg = err?.response?.data.detail[0].msg || `${t('error.notprosess')}`;
                refAlert.current.showMessageBox(t('error.title'), msg);
              },
            });
          }
          
          isLoadingRef.current = false;
          setEditPost(defaultPostVlues);
          closeEditDialog(true); // 다이얼로그 닫기
        }
      });
    }
  };

  // 사용자 삭제 핸들러
  const handleDeletePost = (index) => {
    if(auth.userId === undefined || auth.userId === null || auth.userId === '') {
      refMessage.openMessage(`${t('error.notlogin')}`);
      setTimeout(() => {
        closeEditDialog(false);
        navigate(`/login`);
      }, process.env.REACT_APP_REDIRECT_TIME_WAIT);
    }
    else if (isValidation()) {

      refConfirm.current.showMessageBox(`${t('button.delete')}`, `${t('alert.delete')}`, (result) => {
        if(result) {
          if (isLoadingRef.current)  return;
          isLoadingRef.current = true;

          // 삭제하고
          deleteMmeber(editPost.Email, {
            onSuccess: () => {
              refAlert.current.showMessageBox(t('button.delete'), t('message.deletecomplet'));
            },
          });
          
          isLoadingRef.current = false;
          setEditPost(defaultPostVlues);
          closeEditDialog(true);; // 다이얼로그 닫기
        }
      });
      
    }
  };

  const useHandleCheckRegNo = (regno) => {
    const { regnoData } = useCheckCompanyRegNoPost(regno);

    if(regnoData) 
    {
      return regnoData.response_code == '4091';
    }
    else return false;
  }
  const useHandleCheckEmail = (email) => {
    const { emailData } = useCheckMemberEmailPost(email);

    if(emailData) 
    {
      return emailData.response_code == '4091';
    }
    else return false;
  }

  const isValidation = () => {
    debugger;
    if(memberId !== null && (editPost?.MemberId === undefined || editPost?.MemberId === null || editPost?.MemberId < 1))
    {
      refAlert.current.showMessageBox(t('alert.title'), `[${t('field.memberid')}] ${t('error.emptyvalue')}`);
      return false;
    }
    
    if(editPost?.CompanyId === undefined || editPost?.CompanyId === null || editPost?.CompanyId === '')
    {
      refAlert.current.showMessageBox(t('alert.title'), `[${t('field.companyid')}] ${t('error.emptyvalue')}`);
      return false;
    }
    
    if(editPost?.MemberName === undefined || editPost?.MemberName === null || editPost?.MemberName === '')
    {
      refAlert.current.showMessageBox(t('alert.title'), `[${t('field.membername')}] ${t('error.emptyvalue')}`);
      return false;
    }
    if(editPost?.MemberType !== 'system' && editPost?.MemberType !== 'master' && editPost?.MemberType !== 'basic')
    {
      refAlert.current.showMessageBox(t('alert.title'), `[${t('field.membertype')}] ${t('error.emptyvalue')}`);
      return false;
    }
    
    if(editPost?.WriteRole === undefined || editPost?.WriteRole === null || editPost?.WriteRole === '')
    {
      refAlert.current.showMessageBox(t('alert.title'), `[${t('field.writerole')}] ${t('error.emptyvalue')}`);
      return false;
    }
    if(editPost?.WriteRole !== 'system' && editPost?.WriteRole !== 'master' && editPost?.WriteRole !== 'basic')
    {
      refAlert.current.showMessageBox(t('alert.title'), `[${t('field.writerole')}] ${t('error.emptyvalue')}`);
      return false;
    }
    if(editPost?.Email === undefined || editPost?.Email === null || editPost?.Email === '')
    {
      refAlert.current.showMessageBox(t('alert.title'), `[${t('field.email')}] ${t('error.emptyvalue')}`);
      return false;
    }
    return true;
  };

  // 다이얼로그 닫기
  const handleCloseDialog = () => {
    setEditPost(defaultPostVlues);
    closeEditDialog(false);
  };

  
  React.useEffect(() => {
    return () => {
      setEditPost(defaultPostVlues);
    }
  }, [open]) 

  React.useEffect(() => {
      try {
          if(data?.response_code === 201)
          {
            setEditPost(data?.data || defaultPostVlues);
          }
          else if (data?.message !==null && data?.message?.length > 0)
          {
            setEditPost(defaultPostVlues);
            refMessage?.showMessageBox(`${t('error.title')}`, JSON.stringify(data.message));
          }
          else if(isSuccess === false && (error !== undefined || error !== null || JSON.stringify(error) !== null ))
          {
            setEditPost(defaultPostVlues);
            if (isDebug()) console.log("useQueryBind[BWF]", new Date().toISOString(), error);
          }
      }
      catch(err) {
        console.log("useQueryExce[BWF]", new Date().toISOString(), err);
      }
  }, [isError, isLoading, isFetching, isSuccess, error, data]);

    
  return (
    <Container maxWidth="sm" style={{ marginTop: "10px" }}>

      {/* 사용 정보 다이얼로그 */}
      <Dialog open={open} TransitionComponent={Transition} fullWidth={true} maxWidth={"md"} 
              onClose={handleCloseDialog} 
              scroll="paper"
              aria-labelledby="scroll-dialog-title"
              aria-describedby="scroll-dialog-description"
      >
        <DialogTitle>
          <Typography variant="h5" gutterBottom align="center">
            {pageTitle}
            &nbsp;&nbsp;
            {/* {memberId !== null ?
                    t('button.modify')
                    : t('button.registration')
                } */}
        </Typography>

        </DialogTitle>
        <Box position="absolute" top={0} right={0}>
            <IconButton onClick={() => handleCloseDialog()}>
                <Close />
            </IconButton>
        </Box>
        
        <DialogContent dividers>

          <Container sx={{ p: 1 }}>
            <Grid container alignItems="center" spacing={1} sx={{  '--Grid-borderWidth': '1px',
                                                                      borderColor: 'divider',
                                                                      '& > div.divline': {
                                                                          borderTop: 'var(--Grid-borderWidth) dotted',
                                                                          borderColor: 'divider',
                                                                          lineHeight: '3px',
                                                                          height: '3px',
                                                                      },
              }}>

                <Grid item size={{ xs: 12, sm: 2, md: 2 }} sx={{ }} >
                  <Typography component="div" sx={{ flexGrow: 1, textAlign: "center" }} color="primary">
                    ※ {t('field.membername')}
                  </Typography>
                </Grid>
                <Grid item size={{ xs: 12, sm: 10, md: 10 }} sx={{ paddingRight: '10px'}} >
                  <TextField id="member_reg_membername" label="" variant="outlined" size="small" disabled={true} fullWidth
                          value={editPost.MemberName} onChange={(e) => setEditPost({...editPost, MemberName:e.target.value})} />
                </Grid>

                <Grid item className="divline" size={{ xs: 12, sm: 12, md: 12,  }} />
                <Grid item size={{ xs: 12, sm: 2, md: 2 }} sx={{ }} >
                  <Typography id="membertype-select-small-label" component="div" sx={{ flexGrow: 1, textAlign: "center" }} color="primary">
                    ※ {t('field.membertype')}
                  </Typography>
                </Grid>
                <Grid item size={{ xs: 12, sm: 10, md: 10 }} sx={{ paddingRight: '10px'}} >
                  <FormControl sx={{ m: 0, p: 0, minWidth: 200 }} size="small">
                    <Select
                        labelId="membertype-select-small-label"
                        id="membertype-select-small"
                        placeholder={t('others.selectone')}
                        value={editPost.MemberType || 'basic'}
                        onChange={(e) => setEditPost({...editPost, MemberType:e.target.value}) }
                        size="small"
                        disabled={true}
                    >
                      <MenuItem value="">
                          <em>{t('others.selectone')}</em>
                      </MenuItem>
                      {selectMemberType ? selectMemberType.map(item => {
                        return <MenuItem key={item.value} value={item.value}>{item.name}</MenuItem>
                      })
                      : null
                      }
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item className="divline" size={{ xs: 12, sm: 12, md: 12,  }} />
                <Grid item size={{ xs: 12, sm: 2, md: 2 }} sx={{ }} >
                  <Typography component="div" sx={{ flexGrow: 1, textAlign: "center" }} color="primary">
                    ※ {t('field.email')}
                  </Typography>
                </Grid>
                <Grid item size={{ xs: 12, sm: 10, md: 10 }} sx={{ paddingRight: '10px'}} >
                  <TextField id="member_reg_memberemail" label="" variant="outlined" size="small" disabled={true} fullWidth
                          value={editPost.Email} onChange={(e) => setEditPost({...editPost, Email:e.target.value})} />
                </Grid>

                <Grid item className="divline" size={{ xs: 12, sm: 12, md: 12,  }} />
                <Grid item size={{ xs: 12, sm: 2, md: 2 }} sx={{ }} >
                  <Typography component="div" sx={{ flexGrow: 1, textAlign: "center" }} color="primary">
                    ※ {t('field.did')}
                  </Typography>
                </Grid>
                <Grid item size={{ xs: 12, sm: 10, md: 10 }} sx={{ paddingRight: '10px'}} >
                  <TextField id="member_reg_memberdid" label="" variant="outlined" size="small" disabled={true} fullWidth
                          value={editPost.Did} onChange={(e) => setEditPost({...editPost, Did:e.target.value})} />
                </Grid>

                <Grid item className="divline" size={{ xs: 12, sm: 12, md: 12,  }} />
                <Grid item size={{ xs: 12, sm: 2, md: 2 }} sx={{ }} >
                  <Typography component="div" sx={{ flexGrow: 1, textAlign: "center" }} color="primary">
                    ※ Connection Id
                  </Typography>
                </Grid>
                <Grid item size={{ xs: 12, sm: 10, md: 10 }} sx={{ paddingRight: '10px'}} >
                  <TextField id="member_reg_memberconnectionid" label="" variant="outlined" size="small" disabled={true} fullWidth
                          value={editPost.ConnectionId} onChange={(e) => setEditPost({...editPost, ConnectionId:e.target.value})} />
                </Grid>

                <Grid item className="divline" size={{ xs: 12, sm: 12, md: 12,  }} />
                <Grid item size={{ xs: 12, sm: 2, md: 2 }} sx={{ }} >
                  <Typography component="div" sx={{ flexGrow: 1, textAlign: "center" }} color="primary">
                    ※ Subscription Id
                  </Typography>
                </Grid>
                <Grid item size={{ xs: 12, sm: 10, md: 10 }} sx={{ paddingRight: '10px'}} >
                  <TextField id="member_reg_membersubscriptionid" label="" variant="outlined" size="small" disabled={true} fullWidth
                          value={editPost.SubscriptionId} onChange={(e) => setEditPost({...editPost, SubscriptionId:e.target.value})} />
                </Grid>
                
                <Grid item className="divline" size={{ xs: 12, sm: 12, md: 12,  }} />
                <Grid item size={{ xs: 12, sm: 2, md: 2 }} sx={{ }} >
                  <Typography id="approvalyn-select-small-label" component="div" sx={{ flexGrow: 1, textAlign: "center" }} color="primary">
                      승인 여부 
                  </Typography>
                </Grid>
                <Grid item size={{ xs: 12, sm: 10, md: 10 }} sx={{ paddingRight: '10px'}} >
                  <FormControl sx={{ m: 0, p: 0, minWidth: 200 }} size="small">
                    <Select
                        labelId="approvalyn-select-small-label"
                        id="approvalyn-select-small"
                        placeholder={t('others.selectone')}
                        value={editPost.ApprovalYn || 'N'}
                        onChange={(e) => setEditPost({...editPost, ApprovalYn:e.target.value}) }
                        size="small"
                        disabled={true}
                    >
                      <MenuItem value="">
                          <em>{t('others.selectone')}</em>
                      </MenuItem>
                      {selectApprovalYn ? selectApprovalYn.map(item => {
                        return <MenuItem key={item.value} value={item.value}>{item.name}</MenuItem>
                      })
                      : null
                      }
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item className="divline" size={{ xs: 12, sm: 12, md: 12,  }} />
                <Grid item size={{ xs: 12, sm: 2, md: 2 }} sx={{ }} >
                  <Typography id="useyn-select-small-label" component="div" sx={{ flexGrow: 1, textAlign: "center" }} color="primary">
                      사용 여부 
                  </Typography>
                </Grid>
                <Grid item size={{ xs: 12, sm: 10, md: 10 }} sx={{ paddingRight: '10px'}} >
                  <FormControl sx={{ m: 0, p: 0, minWidth: 200 }} size="small">
                    <Select
                        labelId="useyn-select-small-label"
                        id="useyn-select-small"
                        placeholder={t('others.selectone')}
                        value={editPost.UseYn || 'N'}
                        onChange={(e) => setEditPost({...editPost, UseYn:e.target.value}) }
                        size="small"
                        disabled={true}
                    >
                      <MenuItem value="">
                          <em>{t('others.selectone')}</em>
                      </MenuItem>
                      {selectUseYn ? selectUseYn.map(item => {
                        return <MenuItem key={item.value} value={item.value}>{item.name}</MenuItem>
                      })
                      : null
                      }
                    </Select>
                  </FormControl>
                </Grid>

            </Grid>
          </Container>

        </DialogContent>

        <DialogActions>

          <Button onClick={handleCloseDialog} startIcon={<Cancel />} color="primary" variant="contained" sx={{ paddingLeft: '35px', paddingRight: '35px', marginRight: '30px', marginTop: '5px' }} >
          {t('button.cancel')}
          </Button>
          {memberId !== null && editPost.MemberId !== null && editPost.MemberName !== null && editPost.Did === null && editPost.ApprovalYn === 'N' ?
          <Button onClick={handleDeletePost} startIcon={<Delete />} color="primary" variant="contained" sx={{ paddingLeft: '35px', paddingRight: '35px', marginRight: '30px', marginTop: '5px' }} >
          {t('button.delete')}
          </Button> : null }
          {memberId !== null && editPost.MemberId !== null && editPost.MemberName !== null && editPost.Did !== null && editPost.ApprovalYn === 'Y' ?
          <Button onClick={handleEditPost} startIcon={<Save />} color="secondary" variant="contained" sx={{ paddingLeft: '35px', paddingRight: '35px', marginRight: '30px', marginTop: '5px' }} >
            {t('button.save')}
          </Button> : null }
          {memberId !== null && regFailYn === 'N' && editPost.MemberId !== null && editPost.MemberName !== null && editPost.Did !== null ?
          <Button onClick={() => handleApproval(editPost.MemberId, editPost.MemberName)} startIcon={<Save />} color="secondary" variant="contained" sx={{ paddingLeft: '35px', paddingRight: '35px', marginRight: '30px', marginTop: '5px' }} >
            {t('button.approval')}
          </Button> : null }

        </DialogActions>
      </Dialog>
      <Dialog4Alert ref={refAlert} />
      <Dialog4Confirm ref={refConfirm} />
    </Container>
  );
};

ControlWriteMember.propTypes = {
  memberId: PropTypes.string, 
  open: PropTypes.bool.isRequired, 
  closeEditDialog: PropTypes.func.isRequired, 
  regFailYn: PropTypes.string.isRequired,
  handleApproval: PropTypes.func.isRequired,
  refMessage: PropTypes.object
};